import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import { getApiBaseUrl, getCurrentUser } from '../../utils/api';
import { getAuthToken } from '../../utils/cookies';
import axios from 'axios';

const integrityGuidelines = [
  {
    icon: 'PanelTop',
    title: 'Stay on this tab',
    description: 'Switching tabs or windows during the interview is recorded and can reduce the final score.',
  },
  {
    icon: 'Ban',
    title: 'No copy or paste',
    description: 'Copy, cut, paste, Ctrl/Cmd+C, and Ctrl/Cmd+V are blocked during the interview.',
  },
  {
    icon: 'FileWarning',
    title: 'Penalties applies',
    description: 'Violations appear in the report with penalty.',
  },
];

const IntegrityGuidelinesCard = () => (
  <aside className="w-full rounded-xl bg-white px-5 py-1 sm:px-6">
    <div className="divide-y divide-slate-200/80 dark:divide-[#2a2d36]">
      {integrityGuidelines.map((item) => (
        <div key={item.title} className="flex items-start gap-3.5 py-4 sm:py-5">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <Icon name={item.icon} size={22} />
          </div>
          <div className="min-w-0 pt-0.5">
            <p className="text-[14px] font-bold leading-snug text-slate-950 dark:text-[#ECEFF4]">{item.title}</p>
            <p className="mt-1 text-[13px] font-medium leading-relaxed text-slate-500 [text-wrap:pretty]">{item.description}</p>
          </div>
        </div>
      ))}
    </div>
  </aside>
);

const privacyCommitments = [
  {
    icon: 'ShieldCheck',
    title: 'Data Never Sold',
    description: 'Your interview data is never sold to third parties.',
  },
  {
    icon: 'UserCheck',
    title: 'Candidate Control',
    description: 'Your data is never shared with recruiters unless you choose to share it.',
  },
  {
    icon: 'MicOff',
    title: 'Voice Protection',
    description: 'Audio is processed in real-time and never stored permanently.',
  },
];

const PrivacyCommitmentsCard = () => (
  <div className="w-full rounded-xl bg-white px-5 py-5 sm:px-6">
    <div className="mb-4 flex items-center gap-2">
      <Icon name="Lock" size={16} className="text-emerald-600" />
      <h2 className="text-[14px] font-bold text-slate-950 dark:text-[#ECEFF4]">Privacy & Trust Commitments</h2>
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      {privacyCommitments.map((item) => (
        <div key={item.title} className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
            <Icon name={item.icon} size={18} />
          </div>
          <div className="min-w-0 pt-0.5">
            <p className="text-[13px] font-bold leading-snug text-slate-950 dark:text-[#ECEFF4]">{item.title}</p>
            <p className="mt-0.5 text-[12px] font-medium leading-relaxed text-slate-500 [text-wrap:pretty]">{item.description}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const VerticalMicrophoneMeter = ({ level }) => {
  return (
    <div
      className="relative flex h-7 w-5 shrink-0 items-center justify-center"
      role="meter"
      aria-label="Live microphone intensity"
      aria-valuemin="0"
      aria-valuemax="100"
      aria-valuenow={level}
      aria-valuetext={`${level}% intensity`}
      title={`Microphone intensity: ${level}%`}
    >
      <div className="absolute top-0 h-[18px] w-[11px] overflow-hidden rounded-t-full rounded-b-md border-2 border-emerald-500/70 bg-emerald-500/10" aria-hidden="true">
        <span
          className="absolute inset-0 origin-bottom bg-emerald-500 transition-transform duration-75 motion-reduce:transition-none"
          style={{ transform: `scaleY(${level / 100})` }}
        />
      </div>
      <div className="absolute top-[12px] h-[11px] w-[18px] rounded-b-full border-2 border-t-0 border-emerald-500/70" aria-hidden="true" />
      <div className="absolute bottom-[3px] h-[5px] w-0.5 bg-emerald-500/70" aria-hidden="true" />
      <div className="absolute bottom-0 h-0.5 w-3 rounded-full bg-emerald-500/70" aria-hidden="true" />
    </div>
  );
};

const PreInterviewCheck = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId, config, isChallenge } = location.state || {};

  // Institution-assignment branding: when a placement cell assigned this
  // interview we co-brand the pre-check with their logo + name and surface the
  // assigner's instructions. The logo URL is already absolute when set from the
  // Assigned Interviews page; resolve defensively in case a relative path slips
  // through from another caller.
  const isInstitutionAssignment = Boolean(config?.isInstitutionAssignment);
  const institutionName = config?.institutionName || '';
  const institutionInstructions = config?.instructions || '';
  const institutionLogo = (() => {
    const p = config?.institutionLogoUrl || '';
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;
    return `${getApiBaseUrl()}${p}`;
  })();

  // Business-hiring branding: when a candidate launches from an invite link we
  // co-brand the pre-check with the hiring company's logo + name and the role /
  // round they're interviewing for. Recruiter-neutral copy (no "your institution").
  // The logo URL is already absolute when set from the candidate invite page.
  const isBusinessHiring = Boolean(config?.isBusinessHiring);
  const businessCompanyName = config?.companyName || config?.company || '';
  const businessJobTitle = config?.jobTitle || config?.role || '';
  const businessRoundName = config?.roundName || '';
  const businessLogo = (() => {
    const p = config?.companyLogoUrl || '';
    if (!p) return '';
    if (/^https?:\/\//i.test(p)) return p;
    return `${getApiBaseUrl()}${p}`;
  })();

  const [audioCheck, setAudioCheck] = useState({ status: 'pending', message: 'Checking microphone...' });
  const [connectionCheck, setConnectionCheck] = useState({ status: 'pending', message: 'Connecting to server...' });
  const [sttCheck, setSttCheck] = useState({ status: 'pending', message: 'Checking browser speech recognition support...' });
  const [resumeCheck, setResumeCheck] = useState({ status: 'pending', message: 'Verifying resume...' });
  const [isReady, setIsReady] = useState(false);
  const [audioStream, setAudioStream] = useState(null);
  const [microphoneLevel, setMicrophoneLevel] = useState(0);
  const [uploading, setUploading] = useState(false);
  const wsRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const microphoneSourceRef = useRef(null);
  const audioAnimationFrameRef = useRef(null);
  const audioCheckTimeoutRef = useRef(null);

  const stopAudioAnalysis = (resetLevel = true) => {
    if (audioAnimationFrameRef.current) {
      cancelAnimationFrame(audioAnimationFrameRef.current);
      audioAnimationFrameRef.current = null;
    }
    if (audioCheckTimeoutRef.current) {
      clearTimeout(audioCheckTimeoutRef.current);
      audioCheckTimeoutRef.current = null;
    }
    if (microphoneSourceRef.current) {
      microphoneSourceRef.current.disconnect();
      microphoneSourceRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (resetLevel) setMicrophoneLevel(0);
  };

  const stopAudioStream = () => {
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(track => track.stop());
      audioStreamRef.current = null;
    }
    setAudioStream(null);
  };

  useEffect(() => {
    // Redirect if no sessionId
    if (!sessionId) {
      navigate(isChallenge ? '/dashboard' : '/interview-setup', {
        state: { error: 'No session ID found. Please start the interview again.' }
      });
      return;
    }

    // Start checks when component mounts
    checkSpeechRecognitionSupport();
    checkAudio();
    checkWebSocketConnection();

    if (config?.type === 'resume' || config?.interviewType === 'resume') {
      checkResume();
    } else {
      setResumeCheck({ status: 'success', message: 'Resume not required for this interview type' });
    }

    // Cleanup on unmount
    return () => {
      stopAudioAnalysis(false);
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(track => track.stop());
        audioStreamRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    // Check if all necessary checks are complete
    const isAudioOk = audioCheck.status === 'success';
    const isConnOk = connectionCheck.status === 'success';
    const isSttOk = sttCheck.status === 'success';
    const isResumeOk = resumeCheck.status === 'success';

    if (isAudioOk && isConnOk && isSttOk && isResumeOk) {
      setIsReady(true);
    } else {
      setIsReady(false);
    }
  }, [audioCheck.status, connectionCheck.status, sttCheck.status, resumeCheck.status]);

  const checkAudio = async () => {
    try {
      stopAudioAnalysis();
      stopAudioStream();
      setAudioCheck({ status: 'checking', message: 'Requesting microphone access...' });

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      audioStreamRef.current = stream;
      setAudioStream(stream);

      // Keep the analyser active so the UI can show the live microphone level.
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      microphone.connect(analyser);
      audioContextRef.current = audioContext;
      microphoneSourceRef.current = microphone;

      if (audioContext.state === 'suspended') {
        audioContext.resume().catch(() => {});
      }

      analyser.fftSize = 256;
      const dataArray = new Uint8Array(analyser.fftSize);

      let hasAudio = false;
      let smoothedLevel = 0;
      let lastRenderedLevel = -1;
      let lastRenderTime = 0;

      const updateAudioLevel = (timestamp) => {
        analyser.getByteTimeDomainData(dataArray);
        let sumSquares = 0;
        for (let index = 0; index < dataArray.length; index += 1) {
          const sample = (dataArray[index] - 128) / 128;
          sumSquares += sample * sample;
        }
        const rms = Math.sqrt(sumSquares / dataArray.length);
        const rawLevel = Math.min(100, Math.max(0, Math.round((rms - 0.008) * 620)));
        smoothedLevel = (smoothedLevel * 0.72) + (rawLevel * 0.28);
        const nextLevel = Math.round(smoothedLevel);

        if (nextLevel > 3) hasAudio = true;
        if (timestamp - lastRenderTime >= 50 && nextLevel !== lastRenderedLevel) {
          setMicrophoneLevel(nextLevel);
          lastRenderedLevel = nextLevel;
          lastRenderTime = timestamp;
        }

        audioAnimationFrameRef.current = requestAnimationFrame(updateAudioLevel);
      };
      audioAnimationFrameRef.current = requestAnimationFrame(updateAudioLevel);

      audioCheckTimeoutRef.current = setTimeout(() => {
        audioCheckTimeoutRef.current = null;
        if (hasAudio || stream.getAudioTracks().length > 0) {
          setAudioCheck({
            status: 'success',
            message: 'Microphone is working properly'
          });
        } else {
          setAudioCheck({
            status: 'warning',
            message: 'Microphone detected but no audio input. Please check your microphone.'
          });
        }
      }, 1000);

    } catch (error) {
      console.error('Audio check error:', error);
      let errorMessage = 'Failed to access microphone.';

      if (error.name === 'NotAllowedError') {
        errorMessage = 'Microphone access denied. Please allow microphone access and try again.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No microphone found. Please connect a microphone and try again.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Microphone is being used by another application.';
      }

      setAudioCheck({
        status: 'error',
        message: errorMessage
      });
    }
  };

  const checkResume = async () => {
    try {
      setResumeCheck({ status: 'checking', message: 'Checking your profile for a resume...' });
      const token = getAuthToken();
      if (!token) {
        setResumeCheck({ status: 'error', message: 'You must be logged in to conduct a resume interview.' });
        return;
      }

      const user = await getCurrentUser(token);
      if (user && (user.resume_url || user.resume_data)) {
        setResumeCheck({ status: 'success', message: 'Resume found and verified' });
      } else {
        setResumeCheck({ status: 'error', message: 'No resume found. Please upload one to proceed.' });
      }
    } catch (error) {
      console.error('Resume check error:', error);
      setResumeCheck({ status: 'error', message: 'Failed to verify resume status. Please try again.' });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file.');
      return;
    }

    setUploading(true);
    setResumeCheck({ status: 'checking', message: 'Uploading and parsing resume...' });

    try {
      const token = getAuthToken();
      const formData = new FormData();
      formData.append('resume', file);

      const response = await axios.post(`${getApiBaseUrl()}/auth/resume`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data) {
        setResumeCheck({ status: 'success', message: 'Resume uploaded and parsed successfully!' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      setResumeCheck({ status: 'error', message: error.response?.data?.error || 'Failed to upload resume' });
    } finally {
      setUploading(false);
    }
  };

  const checkWebSocketConnection = () => {
    if (!sessionId) {
      setConnectionCheck({
        status: 'error',
        message: 'No session ID provided. Please go back and start the interview again.'
      });
      return;
    }

    try {
      setConnectionCheck({ status: 'checking', message: 'Establishing WebSocket connection...' });

      const apiBaseUrl = getApiBaseUrl();
      const wsUrl = apiBaseUrl.replace(/^http/, 'ws') + `/ws?sessionId=${sessionId}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      // Local flag — avoids stale closure on connectionCheck state in onclose.
      let connectionSucceeded = false;

      ws.onopen = () => {
        connectionSucceeded = true;
        setConnectionCheck({
          status: 'success',
          message: 'Connected to interview server'
        });

        // Send a test message to verify connection
        ws.send(JSON.stringify({
          type: 'ping',
          timestamp: Date.now()
        }));
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle start message or any initial message
          if (message.type === 'start' || message.type === 'pong') {
            connectionSucceeded = true;
            setConnectionCheck({
              status: 'success',
              message: 'Server connection verified'
            });
          }
        } catch (err) {
          console.error('Error parsing WebSocket message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionCheck({
          status: 'error',
          message: 'Failed to connect to server. Please check your internet connection.'
        });
      };

      ws.onclose = (event) => {
        // Only report an error if we never successfully connected.
        // Normal close (1000) after a successful ping/pong is expected.
        if (!connectionSucceeded && event.code !== 1000) {
          setConnectionCheck({
            status: 'error',
            message: 'Connection closed unexpectedly. Please try again.'
          });
        }
      };

    } catch (error) {
      console.error('WebSocket setup error:', error);
      setConnectionCheck({
        status: 'error',
        message: 'Failed to establish connection. Please try again.'
      });
    }
  };

  const handleRetryAudio = () => {
    setAudioCheck({ status: 'pending', message: 'Checking microphone...' });
    checkAudio();
  };

  const handleRetryConnection = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    setConnectionCheck({ status: 'pending', message: 'Connecting to server...' });
    checkWebSocketConnection();
  };

  const checkSpeechRecognitionSupport = () => {
    const supported = !!(window.SpeechRecognition || window.webkitSpeechRecognition);
    if (supported) {
      setSttCheck({ status: 'success', message: 'Browser speech recognition is available.' });
    } else {
      setSttCheck({ status: 'error', message: 'Browser speech recognition is not available. Use Chrome or Edge for best support.' });
    }
  };

  const handleStartInterview = () => {
    if (isChallenge) {
      try {
        const pendingChallenge = JSON.parse(sessionStorage.getItem('pendingLiveChallenge') || 'null');
        if (pendingChallenge?.sessionId === sessionId) {
          sessionStorage.removeItem('pendingLiveChallenge');
        }
      } catch (error) {
        console.warn('Could not clear pending challenge:', error);
      }
    }

    // Close the test WebSocket connection (it will be recreated in live-interview-panel)
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Navigate to live interview panel
    navigate('/live-interview-panel', {
      state: {
        sessionId,
        config
      }
    });
  };

  const handleGoBack = async () => {
    // Cleanup local resources (mic, audio context, test WS)
    stopAudioAnalysis();
    stopAudioStream();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    // Only end (and refund) the session when the user is backing out of a
    // coin-funded practice session. For institution assignments and challenge
    // sessions the session must stay alive so the student can resume later —
    // calling endInterviewSession would mark it ended in Redis/Postgres and
    // break the resume flow entirely.
    const shouldEndSession = sessionId && !isInstitutionAssignment && !isChallenge && !isBusinessHiring;
    if (shouldEndSession) {
      try {
        const { endInterviewSession } = await import('../../utils/api');
        await endInterviewSession(sessionId);
      } catch (err) {
        console.warn('Failed to end session on go-back (coins may not be refunded):', err);
      }
    }

    // Always clear the local session cache
    if (sessionId) {
      localStorage.removeItem('currentInterviewSession');
    }

    // Navigate back to the right surface
    if (isInstitutionAssignment) {
      navigate('/assigned-interviews');
    } else if (isChallenge) {
      navigate('/dashboard');
    } else {
      navigate('/interview-setup');
    }
  };

  const canStartInterview = isReady;

  const getStatusText = (type, status) => {
    if (status !== 'success') return 'Checking...';
    switch (type) {
      case 'audio': return 'Working';
      case 'connection': return 'Connected';
      case 'stt': return 'Supported';
      case 'resume': return 'Verified';
      default: return 'Ready';
    }
  };

  const getStatusDisplay = (type, status) => {
    if (status === 'success') {
      return (
        <div className="inline-flex min-h-8 items-center rounded-lg bg-emerald-50 px-2.5 text-[12px] font-bold text-emerald-600">
          <Icon name="CheckCircle" size={14} className="mr-1.5" />
          {getStatusText(type, status)}
        </div>
      );
    } else if (status === 'error') {
      return (
        <div className="inline-flex min-h-8 items-center rounded-lg bg-red-50 px-2.5 text-[12px] font-bold text-red-500">
          <Icon name="XCircle" size={14} className="mr-1.5" />
          Failed
        </div>
      );
    } else if (status === 'warning') {
      return (
        <div className="inline-flex min-h-8 items-center rounded-lg bg-amber-50 px-2.5 text-[12px] font-bold text-amber-500">
          <Icon name="AlertCircle" size={14} className="mr-1.5" />
          Warning
        </div>
      );
    }
    return (
      <div className="inline-flex min-h-8 items-center rounded-lg bg-slate-50 px-2.5 text-[12px] font-bold text-slate-400">
        <div className="w-4 h-4 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin mr-1.5" />
        Checking...
      </div>
    );
  };

  return (

    <div className="min-h-screen flex flex-col items-center justify-center bg-[#fdfdfd] px-4 py-6 font-sans text-slate-900">
      <div className="w-full max-w-6xl my-auto">
        <div className="mb-5 flex items-center gap-3.5">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50">
            <Icon name="Mic" size={22} className="text-emerald-600" />
          </div>
          <div>
            <h1 className="text-[24px] font-bold leading-tight text-slate-950 dark:text-[#ECEFF4] [text-wrap:balance]">Pre-Interview Check</h1>
            <p className="mt-1 text-[13px] font-medium text-slate-500 [text-wrap:pretty]">Let's make sure everything is ready before we start.</p>
          </div>
        </div>

        {/* Institution assignment banner: co-branding + assigner instructions */}
        {isInstitutionAssignment && (institutionName || institutionInstructions || institutionLogo) && (
          <div className="mb-5 overflow-hidden rounded-xl border border-emerald-100 dark:border-emerald-500/20 bg-white dark:bg-card">
            <div className="flex items-center gap-3 border-b border-slate-100 dark:border-border bg-emerald-50/60 dark:bg-emerald-500/10 px-5 py-3.5">
              {institutionLogo ? (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 dark:border-border bg-white dark:bg-muted p-1">
                  <img
                    src={institutionLogo}
                    alt={institutionName ? `${institutionName} logo` : 'Institution logo'}
                    className="h-full w-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                  <Icon name="Building2" size={20} />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[11px] font-semibold tracking-wide text-emerald-700 dark:text-emerald-400">Assigned interview</p>
                <p className="truncate text-[15px] font-bold text-slate-950 dark:text-slate-100">
                  {institutionName || 'Your institution'}
                  {config?.campaignName ? (
                    <span className="font-medium text-slate-500 dark:text-slate-400"> · {config.campaignName}</span>
                  ) : null}
                </p>
              </div>
            </div>
            {institutionInstructions && (
              <div className="px-5 py-4">
                <div className="mb-1.5 flex items-center gap-1.5">
                  <Icon name="ClipboardList" size={14} className="text-emerald-600 dark:text-emerald-400" />
                  <p className="text-[12px] font-bold tracking-wide text-slate-500 dark:text-slate-400">
                    Instructions from your institution
                  </p>
                </div>
                <p className="whitespace-pre-line text-[13px] leading-relaxed text-slate-700 dark:text-slate-300 [text-wrap:pretty]">
                  {institutionInstructions}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Business hiring banner: co-branding for a candidate interview. Mirrors
            the institution banner's look but with recruiter-neutral copy — a
            candidate is interviewing WITH a company for a role, not "assigned" by
            "your institution". */}
        {isBusinessHiring && (businessCompanyName || businessJobTitle || businessLogo) && (
          <div className="mb-5 overflow-hidden rounded-xl border border-emerald-100 dark:border-emerald-500/20 bg-white dark:bg-card">
            <div className="flex items-center gap-3 px-5 py-3.5">
              {businessLogo ? (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 dark:border-border bg-white dark:bg-muted p-1">
                  <img
                    src={businessLogo}
                    alt={businessCompanyName ? `${businessCompanyName} logo` : 'Company logo'}
                    className="h-full w-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400">
                  <Icon name="Building2" size={20} />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[11px] font-semibold tracking-wide text-emerald-700 dark:text-emerald-400">
                  {businessCompanyName ? `Interview with ${businessCompanyName}` : 'Interview'}
                </p>
                <p className="truncate text-[15px] font-bold text-slate-950 dark:text-slate-100">
                  {businessJobTitle || 'Your interview'}
                  {businessRoundName ? (
                    <span className="font-medium text-slate-500 dark:text-slate-400"> · {businessRoundName}</span>
                  ) : null}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 items-start lg:grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)] lg:gap-6">
          <section className="w-full">
            {/* Checks Card */}
            <div className="w-full rounded-xl bg-white px-5 py-1 sm:px-6">
          <div className="divide-y divide-slate-100 dark:divide-[#2a2d36]">
            
            {/* Audio Check */}
            <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:py-5">
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                  <Icon name="Mic" size={22} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-950 dark:text-[#ECEFF4] text-[14px] mb-1.5">Microphone Check</h3>
                  <p className="text-slate-500 text-[13px] font-medium">{audioCheck.message}</p>
                  {audioCheck.status === 'error' && (
                    <button onClick={handleRetryAudio} className="mt-2 text-sm text-emerald-600 font-medium hover:underline">
                      Retry
                    </button>
                  )}
                </div>
              </div>
              <div className="flex flex-shrink-0 items-center gap-2.5">
                {audioStream && audioCheck.status === 'success' && (
                  <VerticalMicrophoneMeter level={microphoneLevel} />
                )}
                {getStatusDisplay('audio', audioCheck.status)}
              </div>
            </div>

            {/* Connection Check */}
            <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:py-5">
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                  <Icon name="Database" size={22} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-950 dark:text-[#ECEFF4] text-[14px] mb-1.5">Server Connection</h3>
                  <p className="text-slate-500 text-[13px] font-medium">{connectionCheck.message}</p>
                  {connectionCheck.status === 'error' && (
                    <button onClick={handleRetryConnection} className="mt-2 text-sm text-emerald-600 font-medium hover:underline">
                      Retry
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-shrink-0">
                {getStatusDisplay('connection', connectionCheck.status)}
              </div>
            </div>

            {/* Browser STT Check */}
            <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:py-5">
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                  <Icon name="Activity" size={22} className="text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-bold text-slate-950 dark:text-[#ECEFF4] text-[14px] mb-1.5">Browser STT Support</h3>
                  <p className="text-slate-500 text-[13px] font-medium">{sttCheck.message}</p>
                </div>
              </div>
              <div className="flex-shrink-0">
                {getStatusDisplay('stt', sttCheck.status)}
              </div>
            </div>

            {/* Resume Check (only for resume type) */}
            {(config?.type === 'resume' || config?.interviewType === 'resume') && (
              <div className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between sm:py-5">
                <div className="flex items-center gap-4">
                  <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-emerald-50">
                    <Icon name="FileText" size={22} className="text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-950 dark:text-[#ECEFF4] text-[14px] mb-1.5">Resume Verification</h3>
                    <p className="text-slate-500 text-[13px] font-medium">{resumeCheck.message}</p>
                    {resumeCheck.status === 'error' && (
                      <div className="mt-3 flex flex-col gap-2">
                        <input type="file" ref={fileInputRef} className="hidden" accept=".pdf" onChange={handleFileUpload} />
                        <div className="flex gap-2">
                          <button onClick={() => fileInputRef.current.click()} disabled={uploading} className="text-sm px-4 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-colors disabled:opacity-50">
                            {uploading ? 'Uploading...' : 'Upload Resume'}
                          </button>
                          <button onClick={checkResume} className="text-sm px-4 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium transition-colors">
                            Verify Again
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {getStatusDisplay('resume', resumeCheck.status)}
                </div>
              </div>
            )}
            
          </div>
        </div>

          </section>

          <div className="hidden h-full w-px bg-slate-200/80 dark:bg-[#2a2d36] lg:block" aria-hidden="true" />

          <div className="w-full">
            <IntegrityGuidelinesCard />
          </div>
        </div>

        {/* Privacy & Trust — full width beneath both columns */}
        <div className="mt-5">
          <PrivacyCommitmentsCard />
        </div>

        <div className="mt-6 flex flex-col items-center">
          {/* Action Buttons */}
          <div className="flex w-full justify-center">
            <div className="grid w-full max-w-[560px] grid-cols-1 gap-3 sm:grid-cols-2">
              <button
                onClick={handleGoBack}
                className="min-h-[52px] py-3.5 px-8 bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-[0.98] rounded-xl font-bold transition-[background-color,transform,opacity] flex items-center justify-center text-[14px]"
              >
                <Icon name="ArrowLeft" size={18} className="mr-2" />
                Go Back
              </button>
              <button
                onClick={handleStartInterview}
                disabled={!canStartInterview}
                className={`min-h-[52px] py-3.5 px-8 rounded-xl font-bold transition-[background-color,transform,opacity] flex items-center justify-center text-[14px]
                  ${canStartInterview 
                    ? 'bg-[#0f8954] hover:bg-[#0c7446] active:scale-[0.98] text-white' 
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`}
              >
                <Icon name="Play" size={18} className="mr-2" />
                Start Interview
              </button>
            </div>
          </div>

          {/* Privacy Note */}
         
        </div>
      </div>
    </div>
  );
};

export default PreInterviewCheck;
